import { apiGet, apiPatch, apiPost, apiDelete } from './api-client';

export interface Notification {
  id: string;
  type: 'system' | 'admin_broadcast' | 'admin_direct' | 'achievement' | 'xp_update' | 'feature_request_update';
  title: string;
  message: string;
  targetUserId: string | null;
  sentByAdminId?: string | null;
  category?: string | null;
  actionUrl?: string | null;
  icon?: string | null;
  metadata?: Record<string, any>;
  isRead: boolean;
  readAt?: string | null;
  deliveredViaSse: boolean;
  createdAt: string;
}

/**
 * Get user's notifications
 */
export async function getNotifications(options?: {
  unreadOnly?: boolean;
  limit?: number;
  offset?: number;
}): Promise<Notification[]> {
  const params = new URLSearchParams();
  if (options?.unreadOnly) params.append('unreadOnly', 'true');
  if (options?.limit) params.append('limit', options.limit.toString());
  if (options?.offset) params.append('offset', options.offset.toString());

  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await apiGet<{ notifications: Notification[] }>(`/api/notifications${query}`);
  return response.notifications;
}

/**
 * Get unread notification count
 */
export async function getUnreadCount(): Promise<number> {
  const response = await apiGet<{ count: number }>('/api/notifications/unread-count');
  return response.count;
}

/**
 * Mark notification as read
 */
export async function markAsRead(notificationId: string): Promise<void> {
  await apiPatch(`/api/notifications/${notificationId}/read`, {});
}

/**
 * Mark all notifications as read
 */
export async function markAllAsRead(): Promise<void> {
  await apiPost('/api/notifications/mark-all-read', {});
}

// ============================================
// Admin Operations
// ============================================

/**
 * Send notification to specific user (admin only)
 */
export async function sendNotification(data: {
  targetUserId: string;
  title: string;
  message: string;
  category?: string;
  actionUrl?: string;
  icon?: string;
  metadata?: Record<string, any>;
}): Promise<Notification> {
  const response = await apiPost<{ notification: Notification }>('/api/notifications/admin/send', data);
  return response.notification;
}

/**
 * Broadcast notification to all users (admin only)
 */
export async function broadcastNotification(data: {
  title: string;
  message: string;
  category?: string;
  actionUrl?: string;
  icon?: string;
  metadata?: Record<string, any>;
}): Promise<void> {
  await apiPost('/api/notifications/admin/broadcast', data);
}

/**
 * Delete notification (admin only)
 */
export async function deleteNotification(notificationId: string): Promise<void> {
  await apiDelete(`/api/notifications/admin/${notificationId}`);
}
